import { ProposalLetterhead, ProposalDocumentFooter } from "./ProposalBranding";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileText, ClipboardList, DollarSign, Calendar, Droplets, Target, CheckCircle, Shield, Beaker, Gauge } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";
import type { PowerWashingFormData } from "./PowerWashingProposalForm";
import { calculatePowerWashingCosts } from "./PowerWashingCostCalculator";
import { proposalSectionText } from "@/lib/proposalResultText";

export interface PowerWashingAIResults {
  companyIntroduction: string;
  propertyAssessment: string;
  surfaceAndMethodPlan: string;
  scopeOfWork: string;
  serviceSchedule: string;
  chemistryAndEquipment: string;
  costBreakdown: string;
  safetyAndCompliance: string;
  termsAndConditions: string;
}

interface PowerWashingProposalResultsProps {
  results: PowerWashingAIResults | null;
  isLoading: boolean;
  onResultsChange?: (results: PowerWashingAIResults) => void;
  formData?: PowerWashingFormData | null;
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const sections = [
  { key: "companyIntroduction" as const, title: "A. Company Introduction & Qualifications", icon: FileText },
  { key: "propertyAssessment" as const, title: "B. Property & Surface Assessment", icon: Target },
  { key: "surfaceAndMethodPlan" as const, title: "C. Surface-Specific Method Plan", icon: Droplets },
  { key: "scopeOfWork" as const, title: "D. Detailed Scope of Work", icon: ClipboardList },
  { key: "serviceSchedule" as const, title: "E. Service Schedule", icon: Calendar },
  { key: "chemistryAndEquipment" as const, title: "F. Chemistry & Equipment", icon: Beaker },
  { key: "costBreakdown" as const, title: "G. Cost Breakdown", icon: DollarSign },
  { key: "safetyAndCompliance" as const, title: "H. Safety, EPA & Stormwater Compliance", icon: Shield },
  { key: "termsAndConditions" as const, title: "I. Terms & Conditions", icon: CheckCircle },
];

const PowerWashingProposalResults = ({ results, isLoading, onResultsChange, formData }: PowerWashingProposalResultsProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-lg font-medium">Generating Power Washing Proposal...</p>
        <p className="text-sm mt-1">Building surface plan, chemistry, and cost estimate</p>
      </div>
    );
  }

  if (!results) return null;

  const startEdit = (key: string, value: string) => { setEditingKey(key); setEditValue(value || ""); };
  const saveEdit = () => {
    if (editingKey && results && onResultsChange) onResultsChange({ ...results, [editingKey]: editValue });
    setEditingKey(null);
  };
  const cancelEdit = () => { setEditingKey(null); setEditValue(""); };

  const pricingCard = formData ? (() => {
    const isSqft = formData.pricingMode === "sqft";
    const costs = calculatePowerWashingCosts(formData);
    return (
      <Card data-pdf-block className="border-primary/30 bg-card shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Gauge className="h-4 w-4 shrink-0 text-primary" />
            Pricing Method: {isSqft ? "Per Square Foot" : "Hourly (Crew Rate × Hours)"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {isSqft ? (
              <>
                <div>
                  <p className="text-muted-foreground text-xs">Rate per Sq Ft</p>
                  <p className="text-foreground font-semibold">{fmt(formData.ratePerSqFt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Total Cleaned Area</p>
                  <p className="text-foreground font-semibold">{formData.totalSquareFootage.toLocaleString()} sq ft</p>
                </div>
                <div className="col-span-2 border-t border-border pt-3">
                  <p className="text-muted-foreground text-xs">Labor Calculation</p>
                  <p className="text-foreground font-mono text-sm">
                    {fmt(formData.ratePerSqFt)} × {formData.totalSquareFootage.toLocaleString()} sq ft = <span className="text-primary font-bold">{fmt(costs.laborCost)}</span>
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-muted-foreground text-xs">Crew Rate</p>
                  <p className="text-foreground font-semibold">{fmt(formData.laborRate)}/hour</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Estimated Labor Hours</p>
                  <p className="text-foreground font-semibold">{formData.estimatedLaborHours.toLocaleString()} hrs</p>
                </div>
                <div className="col-span-2 border-t border-border pt-3">
                  <p className="text-muted-foreground text-xs">Labor Calculation</p>
                  <p className="text-foreground font-mono text-sm">
                    {fmt(formData.laborRate)}/hr × {formData.estimatedLaborHours.toLocaleString()} hrs = <span className="text-primary font-bold">{fmt(costs.laborCost)}</span>
                  </p>
                </div>
              </>
            )}
            <div className="col-span-2 border-t border-border pt-3 flex justify-between">
              <span className="text-muted-foreground">Total Service Cost</span>
              <span className="text-primary font-bold text-lg">{fmt(costs.totalServiceCost)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  })() : null;

  return (
    <div id="power-washing-proposal-content" className="proposal-document animate-fade-in">
      <ProposalLetterhead title="Power Washing Service Proposal" />

      {pricingCard}
      {sections.map(({ key, title, icon: Icon }) => {
        const content = proposalSectionText(results[key]);
        if (!content) return null;
        return (
          <Card key={key} data-pdf-block className="proposal-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-start justify-between gap-2 text-foreground text-sm">
                <span className="flex items-start gap-2 min-w-0">
                  <Icon className="h-4 w-4 shrink-0 text-primary" />
                  {title}
                </span>
                {onResultsChange && editingKey !== key && (
                  <Button data-html2canvas-ignore title={`Edit ${title}`} variant="ghost" size="sm" onClick={() => startEdit(key, content)} className="text-muted-foreground hover:text-foreground">
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingKey === key ? (
                <div className="space-y-3">
                  <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[120px] bg-secondary border-border text-foreground" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="gradient-primary text-primary-foreground"><Check className="h-4 w-4 mr-1" /> Save</Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none text-secondary-foreground whitespace-pre-wrap">
                  {content}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      <ProposalDocumentFooter />
    </div>
  );
};

export default PowerWashingProposalResults;
